import "./keyboard.less"
import PresentationQuestion from "../components/media/presentation-question"
import { classNames, hasText } from "utils/helpers"
import { ChangeEvent, useLayoutEffect, useRef, useState } from "react"
import PresentationImage from "../components/media/presentation-image"
import { PlayKeyboardState } from "play/play-model"
import { usePlayState } from "play/hooks/use-play-state"
import { requestNextScreenReducer } from "play/reducers"
import { ScreenType } from "models/tables"
import { useMessages } from "play/language/language"

export default function Keyboard() {
  const {
    screen,
    modifyState,
    finished,
    showCorrectAnswers,
    keyboard,
    lastScreenIsCorrect,
    correctAnswerText,
    isTeacherUrl,
    isPreview,
  } = usePlayState()
  const isLoading = useRef(false)

  function answerChanged(newText: string) {
    modifyState((state) => {
      if (!state.screen) {
        console.error("No screen!")
        return
      }
      if (state.screen.screenType !== ScreenType.Keyboard) {
        console.error("Not keyboard!")
        return
      }
      if (state.finished) {
        // console.log("Finished!");
        return
      }
      if (state.isTeacherUrl) {
        return
      }

      state.keyboard.answer = newText

      state.nextScreenButtonIsActive = hasText(state.keyboard.answer)
    })
  }

  function submitAnswer() {
    if (isLoading.current) return
    isLoading.current = true

    modifyState((state) => {
      requestNextScreenReducer(state)
        .then(() => (isLoading.current = false))
        .catch(() => (isLoading.current = false))
    })
  }

  return (
    <KeyboardScreenView
      questionImage={screen?.questionImage}
      questionText={screen?.questionText}
      questionImageWidth={screen?.questionImageWidth || 0}
      questionImageHeight={screen?.questionImageHeight || 0}
      finished={finished}
      isTeacher={isTeacherUrl}
      showCorrectAnswers={showCorrectAnswers}
      isPreview={isPreview}
      answer={keyboard.answer}
      correctAnswer={correctAnswerText || ""}
      isCorrect={lastScreenIsCorrect}
      onKeyboardTextChanged={answerChanged}
      onSubmitAnswer={submitAnswer}
    />
  )
}

type KeyboardProps = {
  finished: boolean
  showCorrectAnswers: boolean
  questionText: string | undefined
  questionImage: string | undefined
  questionImageWidth: number
  questionImageHeight: number
  answer: PlayKeyboardState["answer"]
  isCorrect: boolean
  correctAnswer: string
  isTeacher: boolean
  isPreview: boolean

  onSubmitAnswer(): void
  onKeyboardTextChanged(newText: string): void
}

export function KeyboardScreenView(props: KeyboardProps) {
  const msg = useMessages()
  const {
    questionText,
    questionImage,
    questionImageWidth,
    questionImageHeight,
    answer,
    finished,
    showCorrectAnswers,
    correctAnswer,
    isTeacher,
    isPreview,
    isCorrect,
    onSubmitAnswer,
    onKeyboardTextChanged,
  } = props

  const [forceRedraw, setForceRedraw] = useState(0)
  const imgContainerRef = useRef<HTMLDivElement>(null)
  const zoomContainerRef = useRef<HTMLDivElement>(null)
  const [isZoomed, setIsZoomed] = useState(false)

  function handleTextChanged(event: ChangeEvent<HTMLInputElement>) {
    onKeyboardTextChanged(event.currentTarget.value)
  }

  // force update
  useLayoutEffect(() => {
    setForceRedraw((prev) => prev + 1)
  }, [imgContainerRef.current])

  useLayoutEffect(() => {
    setForceRedraw((prev) => prev + 1)
  }, [isZoomed])

  let correctClass = ""
  if (showCorrectAnswers && !isTeacher) {
    correctClass = isCorrect ? "correct" : "incorrect"
  }

  let showTheCorrectAnswer = false
  if (showCorrectAnswers && isTeacher && hasText(correctAnswer)) {
    showTheCorrectAnswer = true
  } else if (showCorrectAnswers && hasText(correctAnswer) && !isCorrect) {
    showTheCorrectAnswer = true
  }

  return (
    <div
      className={classNames("keyboard-screen play-screen", {
        zoomed: isZoomed,
        w:
          questionImageWidth &&
          questionImageHeight &&
          questionImageWidth > questionImageHeight,
        h:
          questionImageWidth &&
          questionImageHeight &&
          questionImageWidth < questionImageHeight,
        "has-image": hasText(questionImage),
      })}
    >
      <div
        className={classNames("keyboard-top", {
          "has-image": hasText(questionImage),
        })}
        ref={zoomContainerRef}
      >
        {hasText(questionImage) && (
          <div
            ref={imgContainerRef}
            style={{ overflow: "hidden", background: "transparent" }}
          >
            <PresentationImage
              className="keyboard-image"
              url={questionImage || ""}
              imageWidth={questionImageWidth || 0}
              imageHeight={questionImageHeight || 0}
              isZoomed={isZoomed}
              modifyHeight={false}
              toggleZoom={() => {
                setIsZoomed((prev) => !prev)
              }}
              alt={msg.keyboardImageAlt}
              zoomContainer={isZoomed ? zoomContainerRef : imgContainerRef}
            />
          </div>
        )}
        <PresentationQuestion
          className="question-top"
          question={questionText}
        />
      </div>
      <div className="keyboard-bottom">
        <PresentationQuestion
          className="question-bottom"
          question={questionText}
        />

        {isZoomed === false && (
          <div className="input-area">
            <div className={`input-wrapper ${correctClass}`}>
              <input
                className={`input ${correctClass}`}
                autoFocus={!isPreview}
                inputMode={finished ? "none" : undefined}
                readOnly={finished || isTeacher}
                type="text"
                onChange={handleTextChanged}
                value={answer}
                disabled={
                  showCorrectAnswers || isPreview || finished || isTeacher
                }
              />
              <button
                className="next"
                onClick={onSubmitAnswer}
                disabled={
                  finished || !hasText(answer) || isPreview || isTeacher
                }
              >
                <i />
              </button>
            </div>
          </div>
        )}

        {showTheCorrectAnswer && (
          <div className="correct-answer">{correctAnswer}</div>
        )}
      </div>
    </div>
  )
}
